import React, { useState, useEffect, useRef, useMemo } from 'react';

import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import {
  useApiGet,
  useApiSend,
  getApiErrorMessage,
} from '../../../utils/httpClientTs';
import dayjs from 'dayjs';
import {
  getLocalChapterCommittees,
  addLocalChapterCommitteeMember,
  updateLocalChapterCommitteeMember,
  replaceLocalChapterCommitteeMember,
  deleteLocalChapterCommitteeMember,
  getLocalChapterCommitteePositions,
  updateLocalChapterCommittee,
  createLocalChapterCommittee,
} from '../../../endpoints/chaptersTs';
import { groupTypes } from '../../../utils/variables';
import PositionDisplay from '../../../Components/PositionDisplay';
import AddPosition from '../../../Components/AddPosition';
import {
  LocalChapterCommittee,
  LocalChapterCommitteeMemberPost,
  LocalChapterCommitteeMemberPut,
  LocalChapterCommitteeMemberReplace,
  LocalChapterCommitteePut,
  LocalChapterCommitteePost,
} from '../../../types/committee';

interface LCCommitteesProps {
  id: string;
  lcAccess: boolean;
  lcAction?: boolean;
}

interface SaveData {
  id: string;
  positionId?: string;
  committeeId?: string;
  action: 'newAppointment' | 'editDates' | 'removeRecord';
  userId?: string;
  startDate?: string | null;
  endDate?: string | null;
}

const LCCommittees: React.FC<LCCommitteesProps> = ({
  id,
  lcAccess,
  lcAction = false,
}) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorSaving, setErrorSaving] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [filteredCommittees, setFilteredCommittees] = useState<
    LocalChapterCommittee[]
  >([]);

  // Helper function to format date to YYYY-MM-DD format
  const formatDateForAPI = (
    dateString: string | null | undefined
  ): string | null => {
    if (!dateString) return null;
    const date = dayjs(dateString);
    return date.isValid() ? date.format('YYYY-MM-DD') : null;
  };
  const [editingCommittee, setEditingCommittee] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<LocalChapterCommitteePut>>(
    {}
  );
  const [isDataRefreshing, setIsDataRefreshing] = useState<boolean>(false);
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [createData, setCreateData] = useState<
    Partial<LocalChapterCommitteePost>
  >({
    name: '',
    description: '',
    group_type_id: groupTypes.Committee,
    is_active: true,
  });
  const scrollToCommitteeRef = useRef<string | null>(null);
  const savedScrollPositionRef = useRef<number>(0);

  const {
    data: committeesData,
    isPending: isCommitteesPending,
    isFetching: isCommitteesFetching,
    isError: isCommitteesError,
    error: committeesQueryError,
    refetch,
  } = useApiGet(['lc-committees', id], () => getLocalChapterCommittees(id), {
    enabled: true,
    retry: 1,
  });

  const committeesList = useMemo(() => committeesData ?? [], [committeesData]);

  const handleSearch = (searchValue: string) => {
    setSearch(searchValue);
  };

  useEffect(() => {
    setFilteredCommittees(
      committeesList.filter((committee) => {
        const matchesSearch = committee.name
          .toLowerCase()
          .includes(search.toLowerCase());
        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' && committee.is_active) ||
          (statusFilter === 'inactive' && !committee.is_active);
        return matchesSearch && matchesStatus;
      })
    );
  }, [committeesList, search, statusFilter]);

  const { data: committeePositionsData } = useApiGet(
    ['committee-positions'],
    getLocalChapterCommitteePositions,
    {
      enabled: true,
      retry: 1,
    }
  );

  const { mutate: updateCommittee } = useApiSend(
    (
      data: {
        committeeId: string;
        memberId: string;
      } & LocalChapterCommitteeMemberPut
    ) =>
      updateLocalChapterCommitteeMember(data.committeeId, data.memberId, {
        start_date: data.start_date,
        end_date: data.end_date,
      }),
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: Error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: deleteCommittee } = useApiSend(
    (data: { committeeId: string; memberId: string }) =>
      deleteLocalChapterCommitteeMember(data.committeeId, data.memberId),
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: Error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: postCommittee } = useApiSend(
    (data: { committeeId: string } & LocalChapterCommitteeMemberPost) =>
      addLocalChapterCommitteeMember(data.committeeId, {
        user_id: data.user_id,
        start_date: data.start_date,
        end_date: data.end_date,
        position_id: data.position_id,
      }),
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: Error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: replaceCommittee } = useApiSend(
    (
      data: {
        committeeId: string;
        memberId: string;
      } & LocalChapterCommitteeMemberReplace
    ) =>
      replaceLocalChapterCommitteeMember(data.committeeId, data.memberId, {
        user_id: data.user_id,
        start_date: data.start_date,
        end_date: data.end_date,
      }),
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: Error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: updateCommitteeDetails } = useApiSend(
    (data: { committeeId: string } & LocalChapterCommitteePut) =>
      updateLocalChapterCommittee(data.committeeId, {
        name: data.name,
        description: data.description,
        group_type_id: data.group_type_id,
        is_active: data.is_active,
      }),
    (data) => {
      setIsSaving(false);
      setEditingCommittee(null);
      setEditData({});
      setIsDataRefreshing(false);
      refetch();
    },
    (error: Error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error updating committee, if problem persists, please contact support'
      );
      setIsSaving(false);
      setIsDataRefreshing(false);
    }
  );

  const { mutate: createCommittee } = useApiSend(
    (data: LocalChapterCommitteePost) => createLocalChapterCommittee(id, data),
    (data) => {
      setIsSaving(false);
      setShowCreateDialog(false);
      setCreateData({
        name: '',
        description: '',
        group_type_id: groupTypes.Committee,
        is_active: true,
      });
      setIsDataRefreshing(false);
      refetch();
    },
    (error: Error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error creating committee, if problem persists, please contact support'
      );
      setIsSaving(false);
      setIsDataRefreshing(false);
    }
  );

  const onSavePosition = (position: SaveData) => {
    setIsSaving(true);
    setErrorSaving(null);
    if (position.action === 'newAppointment') {
      //Handle post
      if (!position.id) {
        //Handle new appointment
        postCommittee({
          committeeId: position.committeeId!,
          user_id: position.userId!,
          start_date: formatDateForAPI(position.startDate) || '',
          end_date: formatDateForAPI(position.endDate),
          position_id: position.positionId!,
        });
      } else {
        replaceCommittee({
          committeeId: position.committeeId!,
          memberId: position.id,
          user_id: position.userId!,
          start_date: formatDateForAPI(position.startDate) || '',
          end_date: formatDateForAPI(position.endDate),
        });
      }
    } else if (position.action === 'editDates') {
      //Handle patch
      updateCommittee({
        committeeId: position.committeeId!,
        memberId: position.id,
        start_date: formatDateForAPI(position.startDate) || '',
        end_date: formatDateForAPI(position.endDate),
      });
    } else if (position.action === 'removeRecord') {
      //Handle delete
      deleteCommittee({
        committeeId: position.committeeId!,
        memberId: position.id,
      });
    }
  };

  const canEdit = (): boolean => {
    return lcAccess && lcAction;
  };

  const handleEditCommittee = (committee: LocalChapterCommittee) => {
    setEditingCommittee(committee.id);
    setEditData({
      name: committee.name,
      description: committee.description,
      group_type_id: groupTypes.Committee,
      is_active: committee.is_active,
    });
    // Save to refs for persistence
    scrollToCommitteeRef.current = committee.id;
    savedScrollPositionRef.current = window.scrollY;
  };

  const handleSaveCommittee = () => {
    if (editingCommittee && editData.name && editData.description) {
      setIsSaving(true);
      setErrorSaving(null);
      setIsDataRefreshing(true);
      updateCommitteeDetails({
        committeeId: editingCommittee,
        name: editData.name,
        description: editData.description,
        group_type_id: editData.group_type_id!,
        is_active: editData.is_active!,
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingCommittee(null);
    setEditData({});
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
  };

  const handleCreateCommittee = () => {
    setIsSaving(true);
    setErrorSaving(null);
    createCommittee(createData as LocalChapterCommitteePost);
  };

  const handleCancelCreate = () => {
    setShowCreateDialog(false);
    setCreateData({
      name: '',
      description: '',
      group_type_id: groupTypes.Committee,
      is_active: true,
    });
  };

  // Scroll to committee after data refresh
  useEffect(() => {
    const committeeToScrollTo = scrollToCommitteeRef.current;
    const positionToRestore = savedScrollPositionRef.current;

    if (committeeToScrollTo && committeesList.length > 0 && !isDataRefreshing) {
      // Use a longer timeout to ensure everything is rendered
      setTimeout(() => {
        // First restore the saved scroll position
        window.scrollTo({
          top: positionToRestore,
          behavior: 'auto' as ScrollBehavior,
        });

        // Then scroll to the specific committee with a slight delay
        setTimeout(() => {
          const element = document.getElementById(
            `committee-${committeeToScrollTo}`
          );
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 200);

        // Clean up refs
        scrollToCommitteeRef.current = null;
        savedScrollPositionRef.current = 0;
      }, 500);
    }
  }, [committeesList, isDataRefreshing]);

  if (isCommitteesPending) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  return (
    <Grid container>
      {isCommitteesError && (
        <Grid item xs={12} sx={{ px: 1, pt: 1 }}>
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {getApiErrorMessage(committeesQueryError) ||
              'Could not load committees.'}{' '}
            {canEdit()
              ? 'You can still create a committee using the button below.'
              : ''}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => refetch()}
            disabled={isCommitteesFetching}
          >
            Retry
          </Button>
        </Grid>
      )}
      <Grid container spacing={2} sx={{ p: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Search"
            id="search-field"
            size="small"
            value={search}
            fullWidth={true}
            onChange={(event) => handleSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          {canEdit() && (
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateDialog(true)}
              disabled={isSaving}
              fullWidth
              size="small"
            >
              Create Committee
            </Button>
          )}
        </Grid>
      </Grid>
      {filteredCommittees.map((committee) => (
        <Grid item xs={12} md={6} sx={{ p: 1 }} key={committee.id}>
          <Card
            id={`committee-${committee.id}`}
            sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}
          >
            <CardHeader
              title={
                editingCommittee === committee.id ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={editData.name || ''}
                    onChange={(e) =>
                      setEditData({ ...editData, name: e.target.value })
                    }
                    variant="outlined"
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">{committee.name}</Typography>
                    <Chip
                      label={committee.is_active ? 'Active' : 'Inactive'}
                      color={committee.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                )
              }
              subheader={
                editingCommittee === committee.id ? (
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    value={editData.description || ''}
                    onChange={(e) =>
                      setEditData({ ...editData, description: e.target.value })
                    }
                    variant="outlined"
                    sx={{ mt: 1 }}
                  />
                ) : (
                  committee.description
                )
              }
              action={
                canEdit() &&
                editingCommittee !== committee.id && (
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditCommittee(committee)}
                    disabled={isSaving}
                    color="info"
                  >
                    Edit
                  </Button>
                )
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {editingCommittee === committee.id && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={editData.group_type_id || groupTypes.Committee}
                        label="Type"
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            group_type_id: e.target.value,
                          })
                        }
                      >
                        <MenuItem value={groupTypes.Committee}>
                          Committee
                        </MenuItem>
                        <MenuItem value={groupTypes.Crafts}>Crafts</MenuItem>
                        <MenuItem value={groupTypes.Exercis}>Exercise</MenuItem>
                        <MenuItem value={groupTypes.Dining}>Dining</MenuItem>
                        <MenuItem value={groupTypes.Wine}>Wine</MenuItem>
                        <MenuItem value={groupTypes.Book}>Book</MenuItem>
                        <MenuItem value={groupTypes.Game}>Game</MenuItem>
                        <MenuItem value={groupTypes.Coffee}>Coffee</MenuItem>
                        <MenuItem value={groupTypes.Other}>Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={editData.is_active ? 'active' : 'inactive'}
                        label="Status"
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            is_active: e.target.value === 'active',
                          })
                        }
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveCommittee}
                        disabled={
                          isSaving || !editData.name || !editData.description
                        }
                        color="success"
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                        color="secondary"
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current members
                  </Typography>
                </Grid>
                {committee.members &&
                  committee.members.length > 0 &&
                  committee.members
                    .sort((a, b) => a.order - b.order)
                    .map((user) => (
                      <PositionDisplay
                        id={user.id}
                        position={user.position}
                        positionId={user.position_id}
                        committeeId={committee.id}
                        name={user.name}
                        startDate={user.start_date}
                        endDate={user.end_date}
                        canEdit={canEdit()}
                        isSaving={isSaving}
                        errorSavingText={errorSaving}
                        onSave={onSavePosition}
                        userId={user.user_id}
                        pageFrom={'lc-committees'}
                        key={user.id}
                        localChapterId={id}
                      />
                    ))}
                {committee.past_members &&
                  committee.past_members.length > 0 && (
                    <Grid item xs={12}>
                      <Accordion
                        defaultExpanded={false}
                        disableGutters
                        sx={{
                          boxShadow: 'none',
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle2">
                            Past members (last 5 years)
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                          <Grid container spacing={2}>
                            {committee.past_members
                              .sort((a, b) => a.order - b.order)
                              .map((user) => (
                                <PositionDisplay
                                  id={user.id}
                                  position={user.position}
                                  positionId={user.position_id}
                                  committeeId={committee.id}
                                  name={user.name}
                                  startDate={user.start_date}
                                  endDate={user.end_date}
                                  canEdit={false}
                                  isSaving={isSaving}
                                  errorSavingText={errorSaving}
                                  userId={user.user_id}
                                  pageFrom={'lc-committees'}
                                  key={`past-${user.id}`}
                                  localChapterId={id}
                                />
                              ))}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  )}
                {canEdit() && (
                  <Grid item xs={12} sx={{ p: 1 }}>
                    <AddPosition
                      buttonText="Add Position"
                      committeeId={committee.id}
                      positions={committeePositionsData}
                      onAdd={onSavePosition}
                      isSaving={isSaving}
                      savingError={errorSaving}
                      saveSuccess={null}
                      spdChapterId={null}
                      localChapterId={id}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
      {!isCommitteesError &&
        filteredCommittees.length === 0 &&
        committeesList.length === 0 && (
          <Grid item xs={12} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No committees yet.
              {canEdit() ? ' Use Create Committee to add the first one.' : ''}
            </Typography>
          </Grid>
        )}
      {!isCommitteesError &&
        filteredCommittees.length === 0 &&
        committeesList.length > 0 && (
          <Grid item xs={12} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No committees match your search or status filter.
            </Typography>
          </Grid>
        )}

      {/* Create Committee Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={handleCancelCreate}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Committee</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Committee Name"
                value={createData.name}
                onChange={(e) =>
                  setCreateData({ ...createData, name: e.target.value })
                }
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={createData.description}
                onChange={(e) =>
                  setCreateData({ ...createData, description: e.target.value })
                }
                fullWidth
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={createData.is_active ? 'active' : 'inactive'}
                  label="Status"
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      is_active: e.target.value === 'active',
                    })
                  }
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCreate} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateCommittee}
            variant="contained"
            color="success"
            disabled={isSaving || !createData.name || !createData.description}
          >
            Create Committee
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default LCCommittees;
